import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError, formatDateTR } from "../lib/apiClient";
import { ArrowLeft, Printer } from "lucide-react";
import { toast } from "sonner";

/**
 * Sevkiyat Listesi PDF — sadece Genel Liste. Üretim listesinden ayrı.
 */
export default function ShipmentListPrintPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const shipmentId = params.get("shipmentId");
  const backTo = params.get("backTo") || "/sevkiyat-paneli";
  const [rows, setRows] = useState([]);
  const [shipment, setShipment] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!shipmentId) return;
    (async () => {
      try {
        const [{ data: matrix }, { data: sh }] = await Promise.all([
          api.get(`/shipments/${shipmentId}/status-matrix`),
          api.get(`/shipments/${shipmentId}`),
        ]);
        // Flatten each order into rows with categories filled
        const flat = matrix.map(o => {
          const cells = {};
          Object.entries(o.categories || {}).forEach(([cat, v]) => {
            cells[cat] = (v?.total > 0) ? (v.model || "•") : "";
          });
          return {
            order_number: o.order_number,
            customer_name: o.customer_name,
            reference: o.reference || "",
            size: o.size || "",
            fabric: o.fabric || "",
            cells,
            quantity: Object.values(o.categories || {}).reduce((s, c) => s + (c?.total || 0), 0),
          };
        });
        setRows(flat);
        setShipment(sh);
      } catch (e) { toast.error(formatApiError(e)); }
      setLoading(false);
    })();
  }, [shipmentId]);

  const CATS = ["Baza", "Başlık", "Yatak", "Komodin", "Bench"];
  const totals = useMemo(() => {
    return {
      orders: rows.length,
      pieces: rows.reduce((s, r) => s + r.quantity, 0),
    };
  }, [rows]);

  if (!shipmentId) return <div className="p-8 text-slate-500">Bir sevkiyat seçin.</div>;
  if (loading) return <div className="p-8 text-slate-500">Yükleniyor...</div>;

  return (
    <div className="print-page">
      <div className="print-hidden sticky top-0 z-30 bg-white border-b-2 border-slate-900 p-4 flex flex-wrap gap-2 items-center">
        <button onClick={() => navigate(backTo)} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-blue-700 mr-3">
          <ArrowLeft size={16}/> Sevkiyat Paneline Dön
        </button>
        <button onClick={() => window.print()} data-testid="ship-print-btn"
          className="ml-auto inline-flex items-center gap-2 px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase text-sm">
          <Printer size={16}/> Yazdır / PDF
        </button>
      </div>

      <div className="print-sheet p-6 max-w-[1600px] mx-auto">
        <header className="mb-4 border-b-4 border-blue-700 pb-3">
          <div className="flex items-baseline justify-between gap-4">
            <div className="flex items-baseline gap-3">
              <span className="inline-block bg-blue-700 text-white font-display font-black text-xl px-3 py-1 leading-none">M</span>
              <div>
                <div className="font-display font-black text-3xl text-blue-700 leading-none tracking-tight">MARSELLA</div>
                <div className="font-display font-bold text-sm text-slate-700 tracking-[0.25em] uppercase mt-0.5">Bedding · Sevkiyat Listesi</div>
              </div>
            </div>
            <div className="text-right text-xs text-slate-700">
              <div className="font-display font-black text-lg text-slate-900">{shipment?.shipment_number}</div>
              <div><b>Müşteri:</b> {shipment?.customer_name}</div>
              <div><b>Varış:</b> {shipment?.destination}</div>
              <div><b>Tarih:</b> {formatDateTR(shipment?.shipment_date || new Date().toISOString())}</div>
              <div><b>Sipariş:</b> {totals.orders} · <b>Parça:</b> {totals.pieces}</div>
            </div>
          </div>
        </header>

        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-slate-100 border-b-2 border-slate-900 text-xs uppercase tracking-wider">
              <th className="text-left px-2 py-2 border border-slate-400">Sipariş</th>
              <th className="text-left px-2 py-2 border border-slate-400">Müşteri</th>
              <th className="text-left px-2 py-2 border border-slate-400">Referans</th>
              <th className="text-left px-2 py-2 border border-slate-400">Ölçü</th>
              <th className="text-left px-2 py-2 border border-slate-400">Kumaş / Renk</th>
              <th className="text-center px-2 py-2 border border-slate-400">Parça</th>
              {CATS.map(c => <th key={c} className="text-left px-2 py-2 border border-slate-400">{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={6 + CATS.length} className="text-center py-8 text-slate-500">Bu sevkiyata dahil sipariş yok.</td></tr>
            )}
            {rows.map((r, i) => (
              <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                <td className="px-2 py-1.5 border border-slate-300 font-mono font-bold text-blue-800 whitespace-nowrap">{r.order_number}</td>
                <td className="px-2 py-1.5 border border-slate-300 font-semibold">{r.customer_name}</td>
                <td className="px-2 py-1.5 border border-slate-300 font-mono">{r.reference || "—"}</td>
                <td className="px-2 py-1.5 border border-slate-300 font-mono">{r.size || "—"}</td>
                <td className="px-2 py-1.5 border border-slate-300">{r.fabric || "—"}</td>
                <td className="px-2 py-1.5 border border-slate-300 text-center font-mono font-bold">{r.quantity}</td>
                {CATS.map(c => (
                  <td key={c} className={`px-2 py-1.5 border border-slate-300 ${r.cells[c] ? "font-semibold" : "text-slate-300"}`}>
                    {r.cells[c] || "—"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        <div className="print-hidden mt-6 p-3 bg-amber-50 border-2 border-amber-400 text-xs text-amber-900">
          <b>📄 Yazdırma ipucu:</b> Yazdır penceresinde <b>"Diğer ayarlar" → "Üst bilgi ve alt bilgi"</b> seçeneğini <b>kapatın</b>.
        </div>
      </div>

      <style>{`
        @media print {
          .print-hidden { display: none !important; }
          body, html { background: white !important; margin: 0 !important; }
          .print-sheet { padding: 6mm !important; max-width: none !important; }
          table { font-size: 10pt !important; }
          @page { size: A4 landscape; margin: 6mm; }
        }
      `}</style>
    </div>
  );
}
